import React, { useState, Fragment } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Encounter, Patient, LabRequest } from '../../types';
import { FlaskConical, CheckCircle, Printer, XCircle, CalendarClock, History } from 'lucide-react';
import { fbcStructure, urinalysisTests, testosteroneTests, hormonalProfileTests, amhStructure, isAMHTest, hcgStructure, isHCGTest, isHCGAbnormal } from '../../store/labStructures';
export const LaboratoryDashboard: React.FC = () => {
  const {
    encounters,
    patients,
    updateEncounter,
    currentUser,
    logAudit
  } = useData();
  const [selectedRequest, setSelectedRequest] = useState<{
    enc: Encounter;
    lab: LabRequest;
  } | null>(null);
  const [results, setResults] = useState<Record<string, string>>({});
  const [labComments, setLabComments] = useState(''); // Added state for lab comments
  // Modals state
  const [cancelModal, setCancelModal] = useState<{
    isOpen: boolean;
    item: {
      enc: Encounter;
      lab: LabRequest;
    } | null;
    reason: string;
  }>({
    isOpen: false,
    item: null,
    reason: ''
  });
  const [rescheduleModal, setRescheduleModal] = useState<{
    isOpen: boolean;
    item: {
      enc: Encounter;
      lab: LabRequest;
    } | null;
    newDate: string;
    reason: string;
  }>({
    isOpen: false,
    item: null,
    newDate: '',
    reason: ''
  });
  const [showHistory, setShowHistory] = useState(false);
  // Find encounters with pending lab requests
  const pendingRequests = encounters.flatMap(enc => enc.labRequests.filter(lab => lab.status === 'Pending').map(lab => ({
    enc,
    lab
  })));
  const rescheduledRequests = encounters.flatMap(enc => enc.labRequests.filter(lab => lab.status === 'Rescheduled').map(lab => ({
    enc,
    lab
  })));
  const historyRequests = encounters.flatMap(enc => enc.labRequests.filter(lab => lab.status === 'Cancelled' || lab.status === 'Rescheduled').map(lab => ({
    enc,
    lab
  }))).sort((a, b) => {
    const timeA = new Date(a.lab.cancelledAt || a.lab.rescheduledAt || 0).getTime();
    const timeB = new Date(b.lab.cancelledAt || b.lab.rescheduledAt || 0).getTime();
    return timeB - timeA;
  });
  const getPatient = (patientId: string) => patients.find(p => p.id === patientId);
  const handleSelectRequest = (item: {
    enc: Encounter;
    lab: LabRequest;
  }) => {
    setSelectedRequest(item);
    setLabComments(item.lab.labComments || ''); // Initialize labComments from existing labComments
    const initialResults: Record<string, string> = {
      ...(item.lab.results || {})
    };
    const hasFBC = item.lab.tests.includes('Full Blood Count') || item.lab.tests.includes('FBC');
    const hasUrinalysis = item.lab.tests.some(t => t.toLowerCase().includes('urinalysis'));
    const hasTestosterone = item.lab.tests.some(t => t.toLowerCase().includes('testosterone'));
    const hasHormonalProfile = item.lab.tests.some(t => t.toLowerCase().includes('hormonal profile'));
    const hasAMH = item.lab.tests.some(t => isAMHTest(t));
    // Only initialize empty strings for keys that don't already have a saved value
    const ensureKey = (k: string) => {
      if (initialResults[k] === undefined) initialResults[k] = '';
    };
    item.lab.tests.forEach(test => {
      if ((test === 'Full Blood Count' || test === 'FBC') && hasFBC) {
        fbcStructure.forEach(cat => {
          cat.tests.forEach(t => ensureKey(t.name));
        });
      } else if (test.toLowerCase().includes('urinalysis') && hasUrinalysis) {
        urinalysisTests.forEach(t => ensureKey(`UA_${t.name}`));
      } else if (test.toLowerCase().includes('testosterone') && hasTestosterone) {
        testosteroneTests.forEach(t => ensureKey(`TESTO_${t.name}`));
      } else if (test.toLowerCase().includes('hormonal profile') && hasHormonalProfile) {
        hormonalProfileTests.forEach(h => ensureKey(`HP_${h.key}`));
      } else if (isAMHTest(test) && hasAMH) {
        ensureKey(amhStructure.storageKey);
      } else if (isHCGTest(test)) {
        ensureKey(hcgStructure.storageKey);
      } else {
        ensureKey(test);
      }
    });
    if (item.lab.otherTests) ensureKey('Others');
    setResults(initialResults);
  };
  const handleSaveResults = () => {
    if (!selectedRequest || !currentUser) return;
    const {
      enc,
      lab
    } = selectedRequest;
    const updatedLab: LabRequest = {
      ...lab,
      status: 'Completed',
      results,
      labComments,
      performedBy: currentUser.fullName,
      completedAt: new Date().toISOString(),
      completedBy: currentUser.id
    };
    const updatedLabRequests = enc.labRequests.map(l => l.id === lab.id ? updatedLab : l);
    // Check if all labs are completed
    const allLabsCompleted = updatedLabRequests.every(l => l.status === 'Completed');
    const updateData: Partial<Encounter> = {
      labRequests: updatedLabRequests,
      pendingLab: !allLabsCompleted
    };
    // If all labs done and no pending radiology, move back to clinician
    if (allLabsCompleted && !enc.pendingRadiology) {
      updateData.stage = 'CLINICIAN';
    }
    updateEncounter(enc.id, updateData);
    alert('Results saved and sent to Clinician.');
    setSelectedRequest(null);
  };
  const handleCancelConfirm = () => {
    if (!cancelModal.item || !currentUser || !cancelModal.reason.trim()) return;
    const {
      enc,
      lab
    } = cancelModal.item;
    const updatedLab: LabRequest = {
      ...lab,
      status: 'Cancelled',
      cancelledAt: new Date().toISOString(),
      cancelledBy: currentUser.id,
      cancellationReason: cancelModal.reason
    };
    const updatedLabRequests = enc.labRequests.map(l => l.id === lab.id ? updatedLab : l);
    const allLabsCompletedOrCancelled = updatedLabRequests.every(l => l.status === 'Completed' || l.status === 'Cancelled');
    updateEncounter(enc.id, {
      labRequests: updatedLabRequests,
      pendingLab: !allLabsCompletedOrCancelled
    });
    const patient = getPatient(enc.patientId);
    logAudit({
      type: 'lab.cancel',
      encounterId: enc.id,
      patientId: enc.patientId,
      patientName: patient ? `${patient.firstName} ${patient.lastName}` : 'Unknown',
      userId: currentUser.id,
      userName: currentUser.fullName,
      userRole: currentUser.role,
      reason: cancelModal.reason,
      labRequestId: lab.id
    });
    setCancelModal({
      isOpen: false,
      item: null,
      reason: ''
    });
  };
  const handleRescheduleConfirm = () => {
    if (!rescheduleModal.item || !currentUser || !rescheduleModal.reason.trim() || !rescheduleModal.newDate) return;
    const {
      enc,
      lab
    } = rescheduleModal.item;
    const now = new Date().toISOString();
    const newHistoryEntry = {
      from: lab.rescheduledFor || lab.requestedAt,
      to: rescheduleModal.newDate,
      at: now,
      by: currentUser.id,
      reason: rescheduleModal.reason
    };
    const updatedLab: LabRequest = {
      ...lab,
      status: 'Rescheduled',
      rescheduledFor: rescheduleModal.newDate,
      rescheduledAt: now,
      rescheduledBy: currentUser.id,
      rescheduleReason: rescheduleModal.reason,
      rescheduleHistory: [...(lab.rescheduleHistory || []), newHistoryEntry]
    };
    const updatedLabRequests = enc.labRequests.map(l => l.id === lab.id ? updatedLab : l);
    updateEncounter(enc.id, {
      labRequests: updatedLabRequests
    });
    const patient = getPatient(enc.patientId);
    logAudit({
      type: 'lab.reschedule',
      encounterId: enc.id,
      patientId: enc.patientId,
      patientName: patient ? `${patient.firstName} ${patient.lastName}` : 'Unknown',
      userId: currentUser.id,
      userName: currentUser.fullName,
      userRole: currentUser.role,
      reason: rescheduleModal.reason,
      labRequestId: lab.id,
      previousDate: lab.rescheduledFor || lab.requestedAt,
      newDate: rescheduleModal.newDate
    });
    setRescheduleModal({
      isOpen: false,
      item: null,
      newDate: '',
      reason: ''
    });
  };
  const handlePrint = () => {
    window.print();
  };
  if (selectedRequest) {
    const patient = getPatient(selectedRequest.enc.patientId);
    if (!patient) return <div data-id="element-2341">Patient not found</div>;
    const hasFBC = selectedRequest.lab.tests.includes('Full Blood Count') || selectedRequest.lab.tests.includes('FBC');
    const hasUrinalysis = selectedRequest.lab.tests.some(t => t.toLowerCase().includes('urinalysis'));
    const hasTestosterone = selectedRequest.lab.tests.some(t => t.toLowerCase().includes('testosterone'));
    const hasHormonalProfile = selectedRequest.lab.tests.some(t => t.toLowerCase().includes('hormonal profile'));
    const hasAMH = selectedRequest.lab.tests.some(t => isAMHTest(t));
    const hasHCG = selectedRequest.lab.tests.some(t => isHCGTest(t));
    const otherTests = selectedRequest.lab.tests.filter(t => t !== 'Full Blood Count' && t !== 'FBC' && !t.toLowerCase().includes('urinalysis') && !t.toLowerCase().includes('testosterone') && !t.toLowerCase().includes('hormonal profile') && !isAMHTest(t) && !isHCGTest(t));
    return <div className="space-y-6 pb-20" data-id="element-2342">
        <div className="flex justify-between items-center print:hidden sticky top-0 bg-gray-50 z-10 py-4 border-b border-gray-200" data-id="element-2343">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-2344">
            Enter Laboratory Results
          </h1>
          <div className="flex space-x-2" data-id="element-2345">
            <Button variant="outline" onClick={() => setSelectedRequest(null)} data-id="element-2346">
              Back to Queue
            </Button>
            <Button variant="outline" onClick={handlePrint} data-id="element-2347">
              <Printer className="w-4 h-4 mr-2" data-id="element-2348" /> Print Results
            </Button>
            <Button onClick={handleSaveResults} className="bg-yellow-600 hover:bg-yellow-700 text-white" data-id="element-2349">
              <CheckCircle className="w-5 h-5 mr-2" data-id="element-2350" /> Save & Send to Clinician
            </Button>
          </div>
        </div>

        {/* Printable Area */}
        <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-8 print:border-none print:shadow-none print:p-0" data-id="element-2351">
          {/* Print Header */}
          <div className="flex justify-between items-start mb-8 border-b-2 border-black pb-6" data-id="element-2352">
            <div className="flex items-center" data-id="element-2353">
              <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-24 w-24 object-contain mr-4" data-id="element-2354" />
              <div data-id="element-2355">
                <h1 className="text-3xl font-bold text-pink-600 uppercase tracking-wider" data-id="element-2356">
                  BMED CARE
                </h1>
                <p className="text-sm italic text-gray-600" data-id="element-2357">
                  Maternity Care Made Better
                </p>
              </div>
            </div>
            <div className="text-right" data-id="element-2358">
              <p className="font-bold text-xl uppercase tracking-widest border-b-2 border-black pb-1 mb-2" data-id="element-2359">
                LABORATORY REPORT
              </p>
              <p className="text-sm" data-id="element-2360">
                <span className="font-semibold" data-id="element-2361">Date:</span>{' '}
                {new Date().toLocaleDateString()}
              </p>
              <p className="text-sm" data-id="element-2362">
                <span className="font-semibold" data-id="element-2363">File No:</span>{' '}
                {patient.bmcFileNumber}
              </p>
            </div>
          </div>

          {/* Patient Details */}
          <div className="border-2 border-black p-4 mb-8 text-sm space-y-3" data-id="element-2364">
            <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-2365">
              <div className="col-span-8" data-id="element-2366">
                <span className="font-semibold" data-id="element-2367">Patient Name:</span>{' '}
                {patient.firstName} {patient.lastName}
              </div>
              <div className="col-span-2" data-id="element-2368">
                <span className="font-semibold" data-id="element-2369">Age:</span> {patient.age}
              </div>
              <div className="col-span-2" data-id="element-2370">
                <span className="font-semibold" data-id="element-2371">Sex:</span> {patient.sex}
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2" data-id="element-2372">
              <div className="col-span-6" data-id="element-2373">
                <span className="font-semibold" data-id="element-2374">Requested By:</span>{' '}
                {selectedRequest.enc.lastAttendingClinician}
              </div>
              <div className="col-span-6" data-id="element-2375">
                <span className="font-semibold" data-id="element-2376">Requested On:</span>{' '}
                {new Date(selectedRequest.lab.requestedAt).toLocaleString()}
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className="space-y-8" data-id="element-2377">
            {hasFBC && <div data-id="element-2378">
                <h3 className="font-bold text-lg mb-2 uppercase border-b border-gray-300 pb-1" data-id="element-2379">
                  Full Blood Count + Differential (FBC + DC)
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-2380">
                  <thead data-id="element-2381">
                    <tr className="bg-gray-100 border-y-2 border-black" data-id="element-2382">
                      <th className="p-2 text-left font-semibold" data-id="element-2383">
                        Test Description
                      </th>
                      <th className="p-2 text-left font-semibold w-48" data-id="element-2384">
                        Value(s)
                      </th>
                      <th className="p-2 text-left font-semibold" data-id="element-2385">
                        Reference range
                      </th>
                      <th className="p-2 text-left font-semibold" data-id="element-2386">Units</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-2387">
                    {fbcStructure.map(category => <Fragment key={category.category} data-id="element-2388">
                        <tr data-id="element-2389">
                          <td colSpan={4} className="p-2 font-bold bg-gray-50 uppercase pt-4" data-id="element-2390">
                            {category.category}
                          </td>
                        </tr>
                        {category.tests.map(test => <tr key={test.name} className="border-b border-gray-100" data-id="element-2391">
                            <td className="p-2 pl-4" data-id="element-2392">{test.name}</td>
                            <td className="p-2" data-id="element-2393">
                              <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0 font-medium" value={results[test.name] || ''} onChange={e => setResults({
                        ...results,
                        [test.name]: e.target.value
                      })} placeholder="Enter value" data-id="element-2394" />
                            </td>
                            <td className="p-2 text-gray-600" data-id="element-2395">{test.range}</td>
                            <td className="p-2 text-gray-600" data-id="element-2396">{test.unit}</td>
                          </tr>)}
                      </Fragment>)}
                  </tbody>
                </table>
              </div>}

            {hasUrinalysis && <div data-id="element-2397">
                <h3 className="font-bold text-lg mb-3 uppercase text-center border-b-2 border-black pb-1" data-id="element-2398">
                  Urinalysis Report
                </h3>
                <div className="max-w-md mx-auto space-y-2 py-4" data-id="element-2399">
                  {urinalysisTests.map(test => <div key={test.name} className="flex items-center" data-id="element-2400">
                      <span className="w-16 font-bold text-sm" data-id="element-2401">
                        {test.name}
                      </span>
                      <span className="mx-3 text-gray-400" data-id="element-2402">-</span>
                      <input type="text" className="flex-1 p-1 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0 font-medium text-sm" value={results[`UA_${test.name}`] || ''} onChange={e => setResults({
                  ...results,
                  [`UA_${test.name}`]: e.target.value
                })} placeholder="Enter result" data-id="element-2403" />
                    </div>)}
                </div>
                <div className="max-w-md mx-auto mt-8 grid grid-cols-2 gap-4 text-sm" data-id="element-2404">
                  <div data-id="element-2405">
                    <label className="block font-semibold mb-1" data-id="element-2406">
                      Dr.'s Name:
                    </label>
                    <input type="text" className="w-full p-1 border-b border-black focus:outline-none print:border-black" value={results['UA_DR_NAME'] || ''} onChange={e => setResults({
                  ...results,
                  ['UA_DR_NAME']: e.target.value
                })} data-id="element-2407" />
                  </div>
                  <div data-id="element-2408">
                    <label className="block font-semibold mb-1" data-id="element-2409">
                      Signature:
                    </label>
                    <input type="text" className="w-full p-1 border-b border-black focus:outline-none print:border-black" value={results['UA_DR_SIG'] || ''} onChange={e => setResults({
                  ...results,
                  ['UA_DR_SIG']: e.target.value
                })} placeholder="Initials / signature" data-id="element-2410" />
                  </div>
                </div>
              </div>}

            {hasTestosterone && <div data-id="element-2411">
                <h3 className="font-bold text-lg mb-2 uppercase border-b border-gray-300 pb-1" data-id="element-2412">
                  Testosterone
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-2413">
                  <thead data-id="element-2414">
                    <tr className="bg-gray-100 border-y-2 border-black" data-id="element-2415">
                      <th className="p-2 text-left font-semibold" data-id="element-2416">
                        Test Description
                      </th>
                      <th className="p-2 text-left font-semibold w-48" data-id="element-2417">
                        Value(s)
                      </th>
                      <th className="p-2 text-left font-semibold" data-id="element-2418">
                        Reference Range
                      </th>
                      <th className="p-2 text-left font-semibold" data-id="element-2419">Units</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-2420">
                    {testosteroneTests.map(test => <Fragment key={test.name} data-id="element-2421">
                        <tr className="border-b border-gray-100" data-id="element-2422">
                          <td className="p-2 font-medium" data-id="element-2423">{test.name}</td>
                          <td className="p-2" data-id="element-2424">
                            <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0 font-medium" value={results[`TESTO_${test.name}`] || ''} onChange={e => setResults({
                        ...results,
                        [`TESTO_${test.name}`]: e.target.value
                      })} placeholder="Enter value" data-id="element-2425" />
                          </td>
                          <td className="p-2 text-gray-600" data-id="element-2426">
                            <div data-id="element-2427">Males: {test.rangeMale}</div>
                            <div data-id="element-2428">Females: {test.rangeFemale}</div>
                          </td>
                          <td className="p-2 text-gray-600" data-id="element-2429">{test.unit}</td>
                        </tr>
                        <tr className="border-b border-gray-200 bg-gray-50" data-id="element-2430">
                          <td colSpan={4} className="p-2 text-xs text-gray-500 italic" data-id="element-2431">
                            Method: {test.method}
                          </td>
                        </tr>
                      </Fragment>)}
                  </tbody>
                </table>
              </div>}

            {hasHormonalProfile && <div data-id="element-2432">
                <h3 className="font-bold text-lg mb-2 uppercase border-b border-gray-300 pb-1" data-id="element-2433">
                  Hormonal Profile
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-2434">
                  <thead data-id="element-2435">
                    <tr className="bg-gray-100 border-y-2 border-black" data-id="element-2436">
                      <th className="p-2 text-left font-semibold" data-id="element-2437">
                        Test Description
                      </th>
                      <th className="p-2 text-left font-semibold w-40" data-id="element-2438">
                        Value(s)
                      </th>
                      <th className="p-2 text-left font-semibold" data-id="element-2439">
                        Reference Range
                      </th>
                      <th className="p-2 text-left font-semibold w-20" data-id="element-2440">
                        Units
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-2441">
                    {hormonalProfileTests.map(hormone => <Fragment key={hormone.key} data-id="element-2442">
                        <tr className="bg-gray-50" data-id="element-2443">
                          <td colSpan={4} className="p-2 font-bold uppercase" data-id="element-2444">
                            {hormone.name}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-100 align-top" data-id="element-2445">
                          <td className="p-2 font-medium" data-id="element-2446">{hormone.name}</td>
                          <td className="p-2" data-id="element-2447">
                            <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0 font-medium" value={results[`HP_${hormone.key}`] || ''} onChange={e => setResults({
                        ...results,
                        [`HP_${hormone.key}`]: e.target.value
                      })} placeholder="Enter value" data-id="element-2448" />
                          </td>
                          <td className="p-2 text-gray-600 text-xs" data-id="element-2449">
                            {hormone.ranges.map(r => <div key={r.label} data-id="element-2450">
                                <span className="font-medium" data-id="element-2451">{r.label}:</span>{' '}
                                {r.value}
                              </div>)}
                          </td>
                          <td className="p-2 text-gray-600" data-id="element-2452">{hormone.unit}</td>
                        </tr>
                        <tr className="border-b border-gray-200 bg-gray-50" data-id="element-2453">
                          <td colSpan={4} className="p-2 text-xs text-gray-500 italic" data-id="element-2454">
                            Method: {hormone.method}
                          </td>
                        </tr>
                      </Fragment>)}
                  </tbody>
                </table>
              </div>}

            {hasAMH && <div data-id="element-2455">
                <h3 className="font-bold text-lg mb-2 uppercase border-b border-gray-300 pb-1" data-id="element-2456">
                  Anti Mullerian Hormone (AMH)
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-2457">
                  <thead data-id="element-2458">
                    <tr className="bg-gray-100 border-y-2 border-black" data-id="element-2459">
                      <th className="p-2 text-left font-semibold" data-id="element-2460">
                        Test Description
                      </th>
                      <th className="p-2 text-left font-semibold w-40" data-id="element-2461">
                        Value(s)
                      </th>
                      <th className="p-2 text-left font-semibold w-40" data-id="element-2462">
                        Reference Range
                      </th>
                      <th className="p-2 text-left font-semibold w-20" data-id="element-2463">
                        Units
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-2464">
                    <tr className="bg-gray-50" data-id="element-2465">
                      <td colSpan={4} className="p-2 font-bold uppercase" data-id="element-2466">
                        Anti Mullerian Hormone (AMH)
                      </td>
                    </tr>
                    <tr className="border-b border-gray-100 align-top" data-id="element-2467">
                      <td className="p-2 font-medium" data-id="element-2468">{amhStructure.name}</td>
                      <td className="p-2" data-id="element-2469">
                        <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0 font-medium" value={results[amhStructure.storageKey] || ''} onChange={e => setResults({
                      ...results,
                      [amhStructure.storageKey]: e.target.value
                    })} placeholder="Enter value" data-id="element-2470" />
                      </td>
                      <td className="p-2 text-gray-600 text-xs" data-id="element-2471">
                        <div className="font-medium mb-1" data-id="element-2472">By age (ng/mL):</div>
                        {amhStructure.ageRanges.map(a => <div key={a.ageBand} data-id="element-2473">
                            <span className="font-medium" data-id="element-2474">{a.ageBand}:</span>{' '}
                            {a.range}
                          </div>)}
                      </td>
                      <td className="p-2 text-gray-600" data-id="element-2475">{amhStructure.unit}</td>
                    </tr>
                    <tr className="border-b border-gray-200 bg-gray-50" data-id="element-2476">
                      <td colSpan={4} className="p-2 text-xs text-gray-500 italic" data-id="element-2477">
                        Method: {amhStructure.method}
                      </td>
                    </tr>
                    <tr data-id="element-2478">
                      <td colSpan={4} className="p-2 text-xs text-gray-600 bg-amber-50 border-t border-amber-200" data-id="element-2479">
                        <p className="font-bold uppercase mb-1" data-id="element-2480">
                          Interpretation
                        </p>
                        <ul className="list-disc pl-5 space-y-0.5" data-id="element-2481">
                          {amhStructure.interpretation.map((line, i) => <li key={i} data-id="element-2482">{line}</li>)}
                        </ul>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>}

            {hasHCG && <div data-id="element-2483">
                <h3 className="font-bold text-lg mb-2 uppercase border-b border-gray-300 pb-1" data-id="element-2484">
                  Beta HCG
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-2485">
                  <thead data-id="element-2486">
                    <tr className="bg-gray-100 border-y-2 border-black" data-id="element-2487">
                      <th className="p-2 text-left font-semibold" data-id="element-2488">
                        Test Description
                      </th>
                      <th className="p-2 text-left font-semibold w-40" data-id="element-2489">
                        Value(s)
                      </th>
                      <th className="p-2 text-left font-semibold w-72" data-id="element-2490">
                        Reference Range
                      </th>
                      <th className="p-2 text-left font-semibold w-20" data-id="element-2491">
                        Units
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-2492">
                    <tr className="bg-gray-50" data-id="element-2493">
                      <td colSpan={4} className="p-2 font-bold uppercase" data-id="element-2494">
                        Beta HCG
                      </td>
                    </tr>
                    <tr className="border-b border-gray-100 align-top" data-id="element-2495">
                      <td className="p-2 font-medium" data-id="element-2496">{hcgStructure.name}</td>
                      <td className="p-2" data-id="element-2497">
                        <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0 font-medium" value={results[hcgStructure.storageKey] || ''} onChange={e => setResults({
                      ...results,
                      [hcgStructure.storageKey]: e.target.value
                    })} placeholder="Enter value" data-id="element-2498" />
                      </td>
                      <td className="p-2 text-gray-600 text-xs" data-id="element-2499">
                        {hcgStructure.ranges.map(r => <div key={r.label} data-id="element-2500">
                            <span className="font-medium" data-id="element-2501">{r.label}:</span>{' '}
                            {r.range}
                          </div>)}
                      </td>
                      <td className="p-2 text-gray-600" data-id="element-2502">{hcgStructure.unit}</td>
                    </tr>
                    <tr className="border-b border-gray-200 bg-gray-50" data-id="element-2503">
                      <td colSpan={4} className="p-2 text-xs text-gray-500 italic" data-id="element-2504">
                        Method: {hcgStructure.method}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>}

            {(otherTests.length > 0 || selectedRequest.lab.otherTests) && <div data-id="element-2505">
                <h3 className="font-bold text-lg mb-2 uppercase border-b border-gray-300 pb-1" data-id="element-2506">
                  Other Investigations
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-2507">
                  <thead data-id="element-2508">
                    <tr className="bg-gray-100 border-y-2 border-black" data-id="element-2509">
                      <th className="p-2 text-left font-semibold w-1/2" data-id="element-2510">
                        Test Description
                      </th>
                      <th className="p-2 text-left font-semibold w-1/2" data-id="element-2511">
                        Result
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-2512">
                    {otherTests.map(test => <tr key={test} className="border-b border-gray-100" data-id="element-2513">
                        <td className="p-2 font-medium" data-id="element-2514">{test}</td>
                        <td className="p-2" data-id="element-2515">
                          <input type="text" className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0 font-medium" value={results[test] || ''} onChange={e => setResults({
                      ...results,
                      [test]: e.target.value
                    })} placeholder="Enter result..." data-id="element-2516" />
                        </td>
                      </tr>)}
                    {selectedRequest.lab.otherTests && <tr className="border-b border-gray-100" data-id="element-2517">
                        <td className="p-2 font-medium" data-id="element-2518">
                          Others: {selectedRequest.lab.otherTests}
                        </td>
                        <td className="p-2" data-id="element-2519">
                          <input type="text" className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0 font-medium" value={results['Others'] || ''} onChange={e => setResults({
                      ...results,
                      ['Others']: e.target.value
                    })} placeholder="Enter result..." data-id="element-2520" />
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>}
          </div>

          {/* Comments / Observations / Interpretation */}
          <div className="mt-8" data-id="element-2521">
            <h3 className="font-bold text-lg mb-2 uppercase border-b border-gray-300 pb-1" data-id="element-2522">
              Comments / Observations / Interpretation
            </h3>
            <textarea className="w-full border border-gray-300 rounded-md p-3 focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0" rows={4} placeholder="Enter any additional observations, interpretations, or comments..." value={labComments} onChange={e => setLabComments(e.target.value)} // Handle input for labComments
          data-id="element-2523" />
          </div>

          {/* Footer Signatures */}
          <div className="mt-16 pt-8 border-t border-gray-300" data-id="element-2524">
            <div className="flex justify-between items-end" data-id="element-2525">
              <div className="w-64" data-id="element-2526">
                <p className="text-sm font-semibold mb-8" data-id="element-2527">
                  Laboratory Technologist:
                </p>
                <div className="border-b border-black" data-id="element-2528"></div>
                <p className="text-sm mt-2 font-medium" data-id="element-2529">
                  {currentUser?.fullName}
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-2530">
                  BMED Laboratory Dept
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>;
  }
  return <div className="space-y-6 print:hidden" data-id="element-2531">
      <div className="flex justify-between items-center" data-id="element-2532">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-2533">
          Laboratory Dashboard
        </h1>
        <div className="flex space-x-2" data-id="element-2534">
          <Badge variant="warning" className="text-sm px-3 py-1" data-id="element-2535">
            {pendingRequests.length} Pending
          </Badge>
          {rescheduledRequests.length > 0 && <Badge variant="info" className="text-sm px-3 py-1 bg-orange-100 text-orange-800" data-id="element-2536">
              {rescheduledRequests.length} Rescheduled
            </Badge>}
        </div>
      </div>

      <div className="space-y-6" data-id="element-2537">
        <Card title="Pending Requests" noPadding data-id="element-2538">
          <ul className="divide-y divide-gray-200" data-id="element-2539">
            {pendingRequests.length > 0 ? pendingRequests.map(item => {
            const patient = getPatient(item.enc.patientId);
            if (!patient) return null;
            return <li key={item.lab.id} className="p-4 hover:bg-gray-50 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4" data-id="element-2540">
                    <div className="flex items-center" data-id="element-2541">
                      <div className="bg-yellow-100 p-2 rounded-full mr-4" data-id="element-2542">
                        <FlaskConical className="h-6 w-6 text-yellow-600" data-id="element-2543" />
                      </div>
                      <div data-id="element-2544">
                        <p className="text-sm font-medium text-blue-600" data-id="element-2545">
                          {patient.bmcFileNumber}
                        </p>
                        <p className="text-lg font-bold text-gray-900" data-id="element-2546">
                          {patient.firstName} {patient.lastName}
                        </p>
                        <p className="text-sm text-gray-500" data-id="element-2547">
                          Tests: {item.lab.tests.join(', ')}{' '}
                          {item.lab.otherTests ? `+ ${item.lab.otherTests}` : ''}
                        </p>
                        <p className="text-xs text-gray-400 mt-1" data-id="element-2548">
                          Requested:{' '}
                          {new Date(item.lab.requestedAt).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 justify-end" data-id="element-2549">
                      <Button size="sm" variant="outline" onClick={() => setRescheduleModal({
                  isOpen: true,
                  item,
                  newDate: '',
                  reason: ''
                })} data-id="element-2550">
                        <CalendarClock className="w-4 h-4 mr-1" data-id="element-2551" /> Reschedule
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setCancelModal({
                  isOpen: true,
                  item,
                  reason: ''
                })} data-id="element-2552">
                        <XCircle className="w-4 h-4 mr-1" data-id="element-2553" /> Cancel
                      </Button>
                      <Button onClick={() => handleSelectRequest(item)} className="bg-yellow-600 hover:bg-yellow-700 text-white" data-id="element-2554">
                        Enter Results
                      </Button>
                    </div>
                  </li>;
          }) : <li className="p-12 text-center text-gray-500" data-id="element-2555">
                <FlaskConical className="mx-auto h-12 w-12 text-gray-300 mb-4" data-id="element-2556" />
                <p className="text-lg font-medium text-gray-900" data-id="element-2557">
                  No pending requests
                </p>
              </li>}
          </ul>
        </Card>

        {rescheduledRequests.length > 0 && <Card title="Rescheduled Requests" noPadding data-id="element-2558">
            <ul className="divide-y divide-gray-200" data-id="element-2559">
              {rescheduledRequests.map(item => {
            const patient = getPatient(item.enc.patientId);
            if (!patient) return null;
            return <li key={item.lab.id} className="p-4 bg-orange-50/50 hover:bg-orange-50 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 border-l-4 border-orange-400" data-id="element-2560">
                    <div className="flex items-center" data-id="element-2561">
                      <div className="bg-orange-100 p-2 rounded-full mr-4" data-id="element-2562">
                        <CalendarClock className="h-6 w-6 text-orange-600" data-id="element-2563" />
                      </div>
                      <div data-id="element-2564">
                        <p className="text-sm font-medium text-blue-600" data-id="element-2565">
                          {patient.bmcFileNumber}
                        </p>
                        <p className="text-lg font-bold text-gray-900" data-id="element-2566">
                          {patient.firstName} {patient.lastName}
                        </p>
                        <p className="text-sm text-gray-500" data-id="element-2567">
                          Tests: {item.lab.tests.join(', ')}{' '}
                          {item.lab.otherTests ? `+ ${item.lab.otherTests}` : ''}
                        </p>
                        <div className="mt-1 flex items-center gap-2" data-id="element-2568">
                          <Badge variant="warning" className="bg-orange-100 text-orange-800" data-id="element-2569">
                            Rescheduled to:{' '}
                            {new Date(item.lab.rescheduledFor!).toLocaleString()}
                          </Badge>
                          <span className="text-xs text-gray-500 italic" data-id="element-2570">
                            Reason: {item.lab.rescheduleReason}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 justify-end" data-id="element-2571">
                      <Button size="sm" variant="outline" onClick={() => setRescheduleModal({
                  isOpen: true,
                  item,
                  newDate: '',
                  reason: ''
                })} data-id="element-2572">
                        <CalendarClock className="w-4 h-4 mr-1" data-id="element-2573" /> Reschedule
                        Again
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setCancelModal({
                  isOpen: true,
                  item,
                  reason: ''
                })} data-id="element-2574">
                        <XCircle className="w-4 h-4 mr-1" data-id="element-2575" /> Cancel
                      </Button>
                      <Button onClick={() => handleSelectRequest(item)} className="bg-yellow-600 hover:bg-yellow-700 text-white" data-id="element-2576">
                        Enter Results
                      </Button>
                    </div>
                  </li>;
          })}
            </ul>
          </Card>}

        <Card data-id="element-2577">
          <div className="flex justify-between items-center cursor-pointer" onClick={() => setShowHistory(!showHistory)} data-id="element-2578">
            <div className="flex items-center gap-2" data-id="element-2579">
              <History className="w-5 h-5 text-gray-500" data-id="element-2580" />
              <h3 className="text-lg font-medium text-gray-900" data-id="element-2581">
                Cancelled & Rescheduled History
              </h3>
            </div>
            <Button variant="outline" size="sm" data-id="element-2582">
              {showHistory ? 'Hide' : 'Show'}
            </Button>
          </div>

          {showHistory && <div className="mt-4 border-t border-gray-200 pt-4" data-id="element-2583">
              {historyRequests.length > 0 ? <ul className="space-y-3" data-id="element-2584">
                  {historyRequests.map((item, idx) => {
              const patient = getPatient(item.enc.patientId);
              const isCancelled = item.lab.status === 'Cancelled';
              return <li key={`${item.lab.id}-${idx}`} className="text-sm bg-gray-50 p-3 rounded border border-gray-200" data-id="element-2585">
                        <div className="flex justify-between mb-1" data-id="element-2586">
                          <span className="font-medium" data-id="element-2587">
                            {patient?.firstName} {patient?.lastName} (
                            {patient?.bmcFileNumber})
                          </span>
                          <span className="text-gray-500 text-xs" data-id="element-2588">
                            {new Date(isCancelled ? item.lab.cancelledAt! : item.lab.rescheduledAt!).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-gray-600 mb-1" data-id="element-2589">
                          Tests:{' '}
                          <span className={isCancelled ? 'line-through text-red-700 opacity-70' : ''} data-id="element-2590">
                            {item.lab.tests.join(', ')} {item.lab.otherTests}
                          </span>
                        </p>
                        <div className="flex items-center gap-2 mt-2" data-id="element-2591">
                          <Badge variant={isCancelled ? 'danger' : 'warning'} className={!isCancelled ? 'bg-orange-100 text-orange-800' : ''} data-id="element-2592">
                            {isCancelled ? 'Cancelled' : 'Rescheduled'}
                          </Badge>
                          <span className="text-gray-600 italic text-xs" data-id="element-2593">
                            Reason:{' '}
                            {isCancelled ? item.lab.cancellationReason : item.lab.rescheduleReason}
                          </span>
                        </div>
                      </li>;
            })}
                </ul> : <p className="text-gray-500 text-sm text-center py-4" data-id="element-2594">
                  No history available.
                </p>}
            </div>}
        </Card>
      </div>

      {/* Modals */}
      {cancelModal.isOpen && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-2595">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" data-id="element-2596">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center text-red-600" data-id="element-2597">
              <XCircle className="w-5 h-5 mr-2" data-id="element-2598" /> Cancel Laboratory Request
            </h3>
            <p className="text-sm text-gray-600 mb-4" data-id="element-2599">
              Are you sure you want to cancel this request? This action will be
              logged in the audit trail.
            </p>
            <div className="mb-4" data-id="element-2600">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2601">
                Reason for cancellation (required)
              </label>
              <textarea className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-red-500 focus:outline-none" rows={3} value={cancelModal.reason} onChange={e => setCancelModal({
            ...cancelModal,
            reason: e.target.value
          })} placeholder="e.g., Clinician requested by mistake, patient refused..." data-id="element-2602" />
            </div>
            <div className="flex justify-end space-x-3" data-id="element-2603">
              <Button variant="outline" onClick={() => setCancelModal({
            isOpen: false,
            item: null,
            reason: ''
          })} data-id="element-2604">
                Go Back
              </Button>
              <Button variant="danger" onClick={handleCancelConfirm} disabled={!cancelModal.reason.trim()} data-id="element-2605">
                Confirm Cancellation
              </Button>
            </div>
          </div>
        </div>}

      {rescheduleModal.isOpen && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-2606">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" data-id="element-2607">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center text-orange-600" data-id="element-2608">
              <CalendarClock className="w-5 h-5 mr-2" data-id="element-2609" /> Reschedule Laboratory
              Request
            </h3>
            <div className="space-y-4" data-id="element-2610">
              <div data-id="element-2611">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2612">
                  New scheduled date & time
                </label>
                <input type="datetime-local" className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-orange-500 focus:outline-none" value={rescheduleModal.newDate} onChange={e => setRescheduleModal({
              ...rescheduleModal,
              newDate: e.target.value
            })} data-id="element-2613" />
              </div>
              <div data-id="element-2614">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2615">
                  Reason for rescheduling (required)
                </label>
                <textarea className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-orange-500 focus:outline-none" rows={3} value={rescheduleModal.reason} onChange={e => setRescheduleModal({
              ...rescheduleModal,
              reason: e.target.value
            })} placeholder="e.g., Reagents out of stock, patient unavailable..." data-id="element-2616" />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6" data-id="element-2617">
              <Button variant="outline" onClick={() => setRescheduleModal({
            isOpen: false,
            item: null,
            newDate: '',
            reason: ''
          })} data-id="element-2618">
                Go Back
              </Button>
              <Button className="bg-orange-600 hover:bg-orange-700 text-white" onClick={handleRescheduleConfirm} disabled={!rescheduleModal.reason.trim() || !rescheduleModal.newDate} data-id="element-2619">
                Confirm Reschedule
              </Button>
            </div>
          </div>
        </div>}
    </div>;
};